import { CommonModule } from '@angular/common';
import { Component, Input, NgZone, OnInit, effect, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

import { Fai, Phone, Plan, ProductFactory, QUOTE_CONTEXTS, QuoteModel, Sim, Simo, Sowo } from '@bytel/bytel-sales';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';

import { AppService } from '@services/app.services';
import { FaiService } from '@services/fai.services';
import { MainCartService } from '@services/main-cart.service';
import { MixedCartService } from '@services/mixed-cart.service';
import { SimService } from '@services/sim.service';

import { SimContextModel } from '@models/sim-context.model';

import { FaiBlockComponent } from '@tunnel/cart/mixed-cart/fai-block/fai-block.component';
import { MobileBlockComponent } from '@tunnel/cart/mixed-cart/mobile-block/mobile-block.component';
import { QuoteDetailComponent } from '@tunnel/cart/quote-detail/quote-detail.component';

import { ISim } from '@interfaces/sim.interace';

import { EMPTY, lastValueFrom, mergeMap, of } from 'rxjs';

@Component({
    selector: 'checkout-mixed-cart',
    standalone: true,
    imports: [
        CommonModule,
        QuoteDetailComponent,
        ReactiveFormsModule,
        DataCyDirective,
        FaiBlockComponent,
        MobileBlockComponent,
    ],
    templateUrl: './mixed-cart.component.html',
    styleUrl: 'mixed-cart.component.scss',
})
export class MixedCartComponent implements OnInit {
    @Input() public cartForm: FormGroup;
    @Input() public isSubmitted: boolean;
    @Input() public mustSuggestMixedCard: boolean = true;

    public suggestMixedCart: boolean = false;
    public isFai: boolean = false;
    public isSimo: boolean = false;
    public isSowo: boolean = false;
    public plan: Plan;
    public isMixedCart: boolean = false;
    public hasSecondQuoteFai: boolean = false;
    public isMobileMQ: boolean = false;
    public simContexts: SimContextModel[];
    public currentSimChoice: ISim;
    public widgetRedirectUrl: string = '/offres-internet/test-eligibilite-panier-mixte';
    public widgetFocus: string = null;

    private mixedCartService: MixedCartService = inject(MixedCartService);
    private cartService: MainCartService = inject(MainCartService);
    private ngZone: NgZone = inject(NgZone);
    private faiService: FaiService = inject(FaiService);
    private simService: SimService = inject(SimService);
    private appService: AppService = inject(AppService);
    private readonly MOBILE_BREAKPOINT: string = '(max-width: 768px)';

    constructor() {
        effect(() => {
            this.simContexts = this.simService.simContexts();

            if (this.simContexts) {
                const isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);
                const quote = isMixedCart
                    ? this.cartService.cartModel.quotes.find((q) => q.context.id === QUOTE_CONTEXTS.ACQUISITION)
                    : this.getQuote(0);

                if (this._shouldUpdateSim(quote)) {
                    this._updateSim(quote);
                }
            }
        });
    }

    public ngOnInit(): void {
        this.initBreakpointMonitoring();
        this.refresh();
        if ((this.plan && !this.isFai) || this.isMixedCart) {
            this.refreshSimContext();
        }

        this.cartService.cartQuoteSubscription.subscribe(() => {
            this.refresh();
        });

        this.cartService.productActionObs
            .pipe(
                mergeMap(async (data) => {
                    if (ProductFactory.Is(data.product, Phone, true) || ProductFactory.Is(data.product, Plan)) {
                        await this.refreshSimContext();
                        return data;
                    }
                    return null;
                }),
                mergeMap((data) => (this._shouldUpdateSim(data?.quote) ? this._updateSim(data?.quote) : EMPTY)),
            )
            .subscribe();
    }

    public onSimChoice(sim: ISim): void {
        this.currentSimChoice = sim;
        this.updateCartSim();
    }

    private getQuote(quoteIndex: number = 0): QuoteModel {
        return this.cartService.getQuote(quoteIndex);
    }

    private initBreakpointMonitoring(): void {
        const mq: MediaQueryList = window.matchMedia(this.MOBILE_BREAKPOINT);
        this.isMobileMQ = mq.matches;

        mq.addEventListener('change', (evt) => {
            if (evt.matches !== this.isMobileMQ) {
                this.ngZone.run(() => {
                    this.isMobileMQ = evt?.matches;
                });
            }
        });
    }
    private async refreshSimContext(): Promise<void> {
        await this.simService.getSimContext(this.cartService.cartModel);
    }

    private async updateCartSim(): Promise<void> {
        const isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);
        const quote = isMixedCart
            ? this.cartService.cartModel.quotes.find((q) => q.context.id === QUOTE_CONTEXTS.ACQUISITION)
            : this.getQuote(0);
        const currentSim = quote.getProductByType(Sim);

        const quoteIndex = this.cartService.getQuoteIndexByContext(quote.context.id as QUOTE_CONTEXTS);
        if (
            currentSim?.gencode !== this.currentSimChoice?.gencode ||
            (currentSim && this.currentSimChoice === undefined)
        ) {
            this.appService.formStatusSubject.next({ ...this.appService.formStatusSubject.getValue(), loading: true });

            await lastValueFrom(
                this.cartService.removeProduct(currentSim, quoteIndex, false).pipe(
                    mergeMap(() => {
                        if (this.currentSimChoice && this.currentSimChoice.gencode !== 'keepMySim') {
                            return this.cartService.addProductByGencode(
                                this.currentSimChoice?.gencode,
                                quoteIndex,
                                false,
                            );
                        }
                        return of(null);
                    }),
                    mergeMap(() => this.cartService.updateSimPrices(quoteIndex)),
                ),
            );

            this.appService.formStatusSubject.next({ ...this.appService.formStatusSubject.getValue(), loading: false });
        }
    }

    private refresh(): void {
        const quote: QuoteModel = this.getQuote(0);
        const quote2: QuoteModel = this.getQuote(1);
        this.plan = quote.getPrincipalProduct<Plan>('Plan');
        const isSecondQuoteFai = quote2 && ProductFactory.Is(quote2.getPrincipalProduct<Plan>('Plan'), Fai, true);
        this.hasSecondQuoteFai = quote2 && quote2.products.length > 0 && isSecondQuoteFai;
        this.isSowo = ProductFactory.Is(this.plan, Sowo, true);
        this.isFai = ProductFactory.Is(this.plan, Fai, true);
        this.isSimo = ProductFactory.Is(this.plan, Simo, true);
        const canHaveMixedCart = this.mixedCartService.canHaveMixedCart(this.cartService.cartModel);
        this.isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);

        if (this.isMixedCart && this.hasSecondQuoteFai) {
            this.faiService.save();
        }

        this.suggestMixedCart =
            this.mustSuggestMixedCard && ((this.mixedCartService.isActive() && canHaveMixedCart) || this.isMixedCart);

        if (this.suggestMixedCart && (this.isSowo || this.isSimo)) {
            this.widgetRedirectUrl = this.mixedCartService.getRedirectUrl(this.plan)?.redirectUrl;
            this.widgetFocus = this.mixedCartService.getRedirectUrl(this.plan)?.focus ?? null;
        }
    }

    private _updateSim = async (quote: QuoteModel): Promise<void> => {
        const quoteIndex = this.cartService.getQuoteIndexByContext(quote.context.id as QUOTE_CONTEXTS);
        await this.cartService.updateSim(quoteIndex, quote.getProductByType(Plan));
    };

    private _shouldUpdateSim(quote: QuoteModel): boolean {
        if (!quote) {
            return false;
        }

        const quoteIndex = this.cartService.getQuoteIndexByContext(quote.context.id as QUOTE_CONTEXTS);
        const sims = this.simContexts[quoteIndex]?.sims;
        const isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);

        // Avoid refresh
        if (this.currentSimChoice && this.currentSimChoice.gencode === quote.getProductByType(Sim)?.gencode) {
            return false;
        }

        const isFai =
            quote.isAcquisitionFix() &&
            quote.getProductByType(Plan) &&
            ProductFactory.Is(quote.getProductByType(Plan), Fai, true);
        if ((isFai && !isMixedCart) || this.currentSimChoice?.gencode === 'keepMySim' || sims?.length <= 0) {
            return false;
        }

        if (!this.simContexts?.length) {
            return false;
        }
        if (quote.isRenewal() && this.currentSimChoice?.gencode !== 'keepMySim') {
            return true;
        }

        if (quote.getProductByType(Plan) && !quote.getProductByType(Sim)) {
            return true;
        }

        return false;
    }
}
